import { tracked } from '@glimmer/tracking'
import Step from 'customer-flows/flow/step'
import FlowPage, { type FlowOverrides } from 'customer-flows/flow/page'

export default class Flow {
  id: string

  @tracked pages: FlowPage[] = []
  @tracked activePage?: FlowPage

  static async create(config: FlowConfig, id: string, overrides?: FlowOverrides) {
    const flow = new Flow()

    for (const page of config.pages) {
      if (page.visible === false) continue

      const newPage = await FlowPage.create(page, config.pages.indexOf(page), overrides)

      if (!newPage) continue

      flow.pages.push(newPage)
    }

    flow.id = id
    flow.setActivePage(flow.pages[0])

    return flow
  }

  get steps(): Array<Step> {
    return this.pages.flatMap((page) => page.steps)
  }

  get activeStep(): Step | undefined {
    return this.activePage?.activeStep
  }

  get activeSteps(): Array<Step> {
    return (
      this.activePage?.steps.filter((step) => {
        // Bypasses the sequential gating entirely, even before step 0 is valid.
        if (step.alwaysVisible) return true

        if (step.pageIndex === undefined || this.activeStep?.pageIndex === undefined) return false

        return step.pageIndex <= this.activeStep?.pageIndex
      }) ?? []
    )
  }

  get stepsInActivePage(): Array<Step> {
    return this.activePage?.steps ?? []
  }

  // Step sequence for next/previous/between lookups, excluding alwaysVisible steps.
  get sequencedSteps(): Array<Step> {
    return this.stepsInActivePage.filter((step) => !step.alwaysVisible)
  }

  get nextPage(): FlowPage | undefined {
    return this.pages[(this.activePage?.flowIndex ?? -1) + 1]
  }

  get previousPage(): FlowPage | undefined {
    return this.pages[(this.activePage?.flowIndex ?? 0) - 1]
  }

  findNextStep(step?: Step): Step | undefined {
    const activeStepToEvaluate = step ?? this.activeStep
    const sequencedSteps = this.sequencedSteps
    const index = sequencedSteps.findIndex((s) => s.id === activeStepToEvaluate?.id)

    return sequencedSteps[index + 1]
  }

  findPreviousStep(step?: Step): Step | undefined {
    const activeStepToEvaluate = step ?? this.activeStep
    const sequencedSteps = this.sequencedSteps
    const index = sequencedSteps.findIndex((s) => s.id === activeStepToEvaluate?.id)

    return sequencedSteps[index - 1]
  }

  findGroupedSteps(step: Step): Step[] {
    return this.activePage?.findGroupedSteps(step) ?? []
  }

  setActivePage(page?: FlowPage): void {
    if (page && page !== this.activePage) {
      this.activePage = page
    }
  }

  setActiveStep(step?: Step): void {
    if (!this.activePage || !step || step.routeName !== this.activePage.routeName) return
    this.activePage.setActiveStep(step)
  }
}
